<template>
  <form class="bwauth-form" novalidate @submit.prevent="submit">
    <div v-if="generalError" class="bwauth-alert bwauth-alert-error">
      {{ generalError }}
    </div>

    <div class="bwauth-grid-2">
      <label class="bwauth-field">
        <span>{{ $t('First Name') }}</span>
        <input
          v-model.trim="form.first_name"
          type="text"
          name="first_name"
          autocomplete="given-name"
          :class="{ 'has-error': fieldError('first_name') }"
          @blur="touch('first_name')"
        >
        <small v-if="fieldError('first_name')">{{ fieldError('first_name') }}</small>
      </label>

      <label class="bwauth-field">
        <span>{{ $t('Last Name') }}</span>
        <input
          v-model.trim="form.last_name"
          type="text"
          name="last_name"
          autocomplete="family-name"
          :class="{ 'has-error': fieldError('last_name') }"
          @blur="touch('last_name')"
        >
        <small v-if="fieldError('last_name')">{{ fieldError('last_name') }}</small>
      </label>
    </div>

    <label class="bwauth-field">
      <span>{{ $t('Email') }}</span>
      <input
        v-model.trim="form.email"
        type="email"
        name="email"
        autocomplete="email"
        :class="{ 'has-error': fieldError('email') }"
        @blur="touch('email')"
      >
      <small v-if="fieldError('email')">{{ fieldError('email') }}</small>
    </label>

    <div class="bwauth-field">
      <span>{{ $t('Phone') }}</span>
      <tel-number-form-input
        v-model.trim="form.phone"
        name="phone"
        :required="true"
        :validation="$v.form.phone"
        :backend-error="fieldError('phone')"
        @phone-validation-error="onPhoneValidationError"
      />
    </div>

    <label class="bwauth-field">
      <span>{{ $t('Password') }}</span>
      <div class="bwauth-password-field">
        <input
          v-model="form.password"
          :type="showPassword ? 'text' : 'password'"
          name="password"
          autocomplete="new-password"
          :class="{ 'has-error': fieldError('password') }"
          @blur="touch('password')"
        >
        <password-toggle v-model="showPassword" />
      </div>
      <small v-if="fieldError('password')">{{ fieldError('password') }}</small>
    </label>

    <div class="bwauth-actions">
      <button type="submit" class="bwauth-primary-button" :disabled="loading">
        <span v-if="loading" class="bwauth-spinner"></span>
        <template v-else>{{ $t('Submit Register') }}</template>
      </button>
    </div>

    <div v-if="showLoginLink" class="bwauth-switch-copy">
      {{ $t('Already have an account?') }}
      <a class="bwauth-link" :href="loginHref" @click="switchToLogin">
        {{ $t('Login') }}
      </a>
    </div>

    <social-auth
      mode="register"
      :redirect-url="redirectUrl"
      @success="handleSocialSuccess"
      @error="handleSocialError"
    />
  </form>
</template>

<script>
import AuthApi from './AuthApi';
import SocialAuth from './SocialAuth.vue';
import TelNumberFormInput from './TelNumberFormInput.vue';
import PasswordToggle from './PasswordToggle.vue';
import { validationMixin } from 'vuelidate';
import { required, email, minLength } from 'vuelidate/lib/validators';

export default {
  mixins: [validationMixin],
  components: {
    SocialAuth,
    TelNumberFormInput,
    PasswordToggle,
  },
  props: {
    redirectUrl: {
      type: String,
      default: null,
    },
    loginHref: {
      type: String,
      default: '/login',
    },
    showLoginLink: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      form: {
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
      },
      touched: {},
      errors: {},
      loading: false,
      showPassword: false,
      phoneValidationError: false,
    };
  },
  validations: {
    form: {
      first_name: { required, minLength: minLength(3) },
      last_name: { required, minLength: minLength(3) },
      email: { required, email },
      phone: { required },
      password: { required, minLength: minLength(6) },
    },
  },
  computed: {
    generalError() {
      if (this.errors && this.errors.message) {
        return this.errors.message;
      }
      return null;
    },
  },
  methods: {
    handleSocialSuccess(user) {
      this.$emit('success', user);
    },
    handleSocialError(error) {
      const response = error && error.response && error.response.data ? error.response.data : {};
      this.errors = response.errors || { message: response.message || this.$t('Server error') };
    },
    touch(field) {
      if (this.$v && this.$v.form && this.$v.form[field]) {
        this.$v.form[field].$touch();
      }
    },
    onPhoneValidationError(value) {
      this.phoneValidationError = value;
    },
    fieldError(field) {
      const control = this.$v && this.$v.form ? this.$v.form[field] : null;
      if (control && control.$dirty && control.$invalid) {
        if (!control.required) {
          return this.$t('validation.required');
        }
        if (field === 'email' && !control.email) {
          return this.$t('validation.email');
        }
        if (['first_name', 'last_name', 'password'].includes(field) && !control.minLength) {
          return this.$t('validation.txtMinLen', { n: control.$params.minLength.min });
        }
      }
      if (this.errors && this.errors[field]) {
        return Array.isArray(this.errors[field]) ? this.errors[field][0] : this.errors[field];
      }
      return null;
    },
    normalizeBackendErrors(response) {
      if (response && response.errors && typeof response.errors === 'object') {
        return response.errors;
      }
      return { message: this.$t('Server error') };
    },
    isValid() {
      this.$v.form.$touch();
      return !this.$v.form.$invalid;
    },
    submit() {
      this.errors = {};

      if (this.phoneValidationError) {
        return;
      }

      if (!this.isValid()) {
        return;
      }

      this.loading = true;
      this.$emit('loading', true);

      AuthApi.register({
        ...this.form,
        redirect_url: this.redirectUrl,
      })
        .then(({ data }) => {
          this.$emit('success', data.user || data);
        })
        .catch((error) => {
          const response = error.response && error.response.data ? error.response.data : {};
          this.errors = this.normalizeBackendErrors(response);
        })
        .finally(() => {
          this.loading = false;
          this.$emit('loading', false);
        });
    },
    switchToLogin(event) {
      if (this.$listeners['switch-to-login'] && event) {
        event.preventDefault();
      }
      this.$emit('switch-to-login');
    },
  },
};
</script>
